import React, { useState } from 'react';
import { SOCIAL_MEDIA_CONFIG } from '../../config/socialMediaConfig';

export const FacebookOAuthDiagnostic: React.FC = () => {
  const [testResults, setTestResults] = useState<any[]>([]);
  
  const runDiagnostics = () => {
    const results = [];
    
    // Test 1: Current domain configuration
    const currentDomain = window.location.hostname;
    const currentOrigin = window.location.origin;
    const currentProtocol = window.location.protocol;
    
    results.push({
      test: 'Domain Configuration',
      status: currentProtocol === 'https:' ? 'PASS' : 'FAIL',
      details: {
        'Current Domain': currentDomain,
        'Current Origin': currentOrigin,
        'Protocol': currentProtocol,
        'Is HTTPS': currentProtocol === 'https:' ? 'Yes' : 'No (Facebook requires HTTPS)'
      }
    });
    
    // Test 2: Redirect URI format
    const redirectUri = SOCIAL_MEDIA_CONFIG.facebook.redirectUri;
    const expectedRedirectUri = `${currentOrigin}/social-media/facebook-callback`;
    
    results.push({
      test: 'Redirect URI Configuration',
      status: redirectUri === expectedRedirectUri ? 'PASS' : 'WARNING',
      details: {
        'Configured URI': redirectUri,
        'Expected URI': expectedRedirectUri,
        'Match': redirectUri === expectedRedirectUri ? 'Yes' : 'No'
      }
    });
    
    // Test 3: WWW subdomain check
    const hasWWW = currentDomain.startsWith('www.');
    const domainWithoutWWW = currentDomain.replace('www.', '');
    const domainWithWWW = hasWWW ? currentDomain : `www.${currentDomain}`;
    
    results.push({
      test: 'Subdomain Configuration',
      status: 'INFO',
      details: {
        'Current': currentDomain,
        'Has WWW': hasWWW ? 'Yes' : 'No',
        'Alternative domain': hasWWW ? domainWithoutWWW : domainWithWWW,
        'Note': 'Add both versions to Facebook if needed'
      }
    });
    
    // Test 4: OAuth URL construction
    const oauthUrl = new URL('https://www.facebook.com/v22.0/dialog/oauth');
    oauthUrl.searchParams.set('client_id', SOCIAL_MEDIA_CONFIG.facebook.appId);
    oauthUrl.searchParams.set('redirect_uri', redirectUri);
    oauthUrl.searchParams.set('response_type', 'code');
    oauthUrl.searchParams.set('scope', 'email,public_profile');
    
    results.push({
      test: 'OAuth URL Construction',
      status: 'INFO',
      details: {
        'App ID': SOCIAL_MEDIA_CONFIG.facebook.appId,
        'OAuth URL': oauthUrl.toString().substring(0, 100) + '...',
        'Redirect URI in URL': redirectUri
      }
    });
    
    setTestResults(results);
  };
  
  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };
  
  return (
    <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
      <h2>Facebook OAuth Diagnostic Tool</h2>
      
      <button 
        onClick={runDiagnostics}
        style={{
          padding: '10px 20px',
          fontSize: '16px',
          backgroundColor: '#1877f2',
          color: 'white',
          border: 'none',
          borderRadius: '6px',
          cursor: 'pointer',
          marginBottom: '2rem'
        }}
      >
        Run Diagnostics
      </button>
      
      {testResults.length > 0 && (
        <div>
          <h3>Diagnostic Results:</h3>
          {testResults.map((result, index) => (
            <div key={index} style={{
              marginBottom: '2rem',
              padding: '1rem',
              border: '1px solid #ddd',
              borderRadius: '8px',
              backgroundColor: '#f9f9f9'
            }}>
              <h4>{result.status} {result.test}</h4>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {Object.entries(result.details).map(([key, value]) => (
                    <tr key={key} style={{ borderBottom: '1px solid #eee' }}>
                      <td style={{ padding: '8px', fontWeight: 'bold', width: '200px' }}>{key}:</td>
                      <td style={{ padding: '8px' }}>
                        <code style={{ backgroundColor: '#fff', padding: '2px 4px', borderRadius: '3px' }}>
                          {String(value)}
                        </code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          
          <div style={{
            marginTop: '2rem',
            padding: '1rem',
            backgroundColor: '#fff3cd',
            border: '1px solid #ffeaa7',
            borderRadius: '8px'
          }}>
            <h4>Copy these EXACT values to Facebook App Settings:</h4>
            
            <div style={{ marginBottom: '1rem' }}>
              <strong>App Domains (Settings → Basic):</strong>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '5px' }}>
                <code style={{ 
                  flex: 1, 
                  padding: '8px', 
                  backgroundColor: '#fff', 
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}>
                  {window.location.hostname}
                </code>
                <button onClick={() => copyToClipboard(window.location.hostname)}>Copy</button>
              </div>
              {!window.location.hostname.startsWith('www.') && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '5px' }}>
                  <code style={{ 
                    flex: 1, 
                    padding: '8px', 
                    backgroundColor: '#fff', 
                    border: '1px solid #ddd',
                    borderRadius: '4px'
                  }}>
                    www.{window.location.hostname}
                  </code>
                  <button onClick={() => copyToClipboard(`www.${window.location.hostname}`)}>Copy</button>
                </div>
              )}
            </div>
            
            <div>
              <strong>Valid OAuth Redirect URIs (Facebook Login → Settings):</strong>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '5px' }}>
                <code style={{ 
                  flex: 1, 
                  padding: '8px', 
                  backgroundColor: '#fff', 
                  border: '1px solid #ddd',
                  borderRadius: '4px'
                }}>
                  {SOCIAL_MEDIA_CONFIG.facebook.redirectUri}
                </code>
                <button onClick={() => copyToClipboard(SOCIAL_MEDIA_CONFIG.facebook.redirectUri)}>Copy</button>
              </div>
            </div>
          </div>
          
          <div style={{
            marginTop: '2rem',
            padding: '1rem',
            backgroundColor: '#d1ecf1',
            border: '1px solid #bee5eb',
            borderRadius: '8px'
          }}>
            <h4>Common Issues to Check:</h4>
            <ol>
              <li><strong>App Mode:</strong> Is your Facebook app in Development or Live mode?</li>
              <li><strong>Test Users:</strong> If in Development mode, are you added as a test user?</li>
              <li><strong>OAuth Settings:</strong> Are "Client OAuth Login" and "Web OAuth Login" enabled?</li>
              <li><strong>Exact Match:</strong> Do the domains match EXACTLY (no trailing slashes)?</li>
              <li><strong>Cache:</strong> Try clearing browser cache or use incognito mode</li>
            </ol>
          </div>
        </div>
      )}
    </div>
  );
};